import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { View } from 'react-native'
import { connect, useSelector } from 'react-redux'

import styled from 'styled-components/native'

import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import {
  getCurrentAppointment,
  getGuardianInfoForEpisode,
} from '@lyrahealth-inc/shared-app-logic/src/features/appointments/utils'
import { getEffectiveRecipientLyraId } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/utils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { isDAEngageProgram } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { Episode, EpisodeStates } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { Outcome } from '@lyrahealth-inc/shared-app-logic/src/models/outcome/Outcome'
import { LTProgramName, ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { ProviderAdminProviderInfo } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import { CloseIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/CloseIcon'
import { GearIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/GearIcon'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { PressableOpacity } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/pressableOpacity/PressableOpacity'
import { SecondaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/secondaryButton/SecondaryButton'
import { Modal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/modal/Modal'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { AddAssessmentModal } from './AddAssessmentModal'
import { ClinicalAssessmentHistory } from './assessmentHistory/ClinicalAssessmentHistory'
import { type FlexibleAssessment } from './data/flexibleAssessmentsApi'
import { EditAssessmentModal } from './EditAssessmentModal'
import ClientOutcomes from '../../clients/individualClient/ClientOutcomes'
import { LT_COACH_ROLES, programConfig, programs, ROLES, SUPERVISOR_ROLES } from '../../common/constants/appConstants'
import { hasRole } from '../../common/utils/utils'
import { getAuthConfig, getAuthIsLTSupervisor } from '../../data/auth/authSelectors'
import { RootState } from '../../data/store'
import { useIsEngageDAProvider } from '../../hooks/useIsEngageDAProvider'
import { getProviderDetailsData } from '../../providers/data/providerSelectors'
import { useAssignmentsData } from '../assignments/data/hooks/useAssignmentsData'
import AssessmentTrackSettingsModal from '../clients/AssessmentTrackSettingsModal'
import ClientProgressPDF from '../clients/clientDetails/clientRecords/ClientProgressPDF'
import { useClientAppointmentsData } from '../clients/clientDetails/data/hooks/useClientAppointmentsData'
import { useSelectedClientData } from '../clients/clientDetails/data/hooks/useSelectedClientData'
import { useAllEpisodesForSelectedClient } from '../episodes/data/hooks/useAllEpisodesForSelectedClient'
import { useSelectedEpisodeForClient } from '../episodes/data/hooks/useSelectedEpisodeForClient'

type ClientProgressProps = {
  providerDetailsData: ProviderAdminProviderInfo
  setClientProgressPDFContent?: (content: React.ReactNode) => void
  providerEpisodesInfo?: { text: string; id: string }[]
  pdfContentStartDate?: string
  pdfContentEndDate?: string
}

interface OuterContainerProps {
  hasMargins: boolean
  theme: ThemeType
  testID?: string
}

const OuterContainer = styled.View.attrs<OuterContainerProps>(({ testID }) => ({
  testID: testID,
}))<OuterContainerProps>(({ hasMargins, theme: { spacing } }) => ({
  width: '100%',
  margin: hasMargins ? spacing['40px'] : '0px',
}))

const ContentContainer = styled.View({
  width: '100%',
})

const TopDropdownContainer = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  zIndex: 10,
})

const LoadingContainer = styled.View({
  display: 'flex',
  justifyContent: 'center',
})

const AligningContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  margin: `${spacing['32px']} 0 0`,
}))
const CloseIconContainer = styled(PressableOpacity)({
  alignSelf: 'flex-end',
})

const AssessmentTrackSettingsContainer = styled.View({
  flex: '1',
  alignItems: 'flex-end',
})

const ClientProgress: React.FC<ClientProgressProps> = ({
  providerDetailsData,
  setClientProgressPDFContent,
  pdfContentStartDate,
  pdfContentEndDate,
  providerEpisodesInfo,
}) => {
  const { formatMessage } = useIntl()
  const roleConfig = useSelector(getAuthConfig)
  const isLtSupervisor: boolean = useSelector(getAuthIsLTSupervisor)
  const [isAssessmentTrackModalOpen, setAssessmentTrackModalOpen] = useState(false)
  const [isAddAssessmentModalOpen, setAddAssessmentModalOpen] = useState(false)
  // Set when the add modal hands off to editing an assessment the client already has.
  const [assessmentToEdit, setAssessmentToEdit] = useState<{
    flexibleAssessment: FlexibleAssessment
    assessmentName: string
  } | null>(null)
  const { enableDAFlexibleAssessment } = useFlags()
  const isEngageDAProvider = useIsEngageDAProvider()
  const { data: clientDetails, isLoading: isLoadingClientDetails } = useSelectedClientData()
  const { isLoading: isLoadingEpisodes } = useAllEpisodesForSelectedClient()
  const { data: clientDetailsSelectedEpisode } = useSelectedEpisodeForClient() as { data: Episode | undefined }
  const {
    data: assignments,
    isError: hasAssignmentsError,
    isFetching: isFetchingAssignments,
    isLoading: isLoadingAssignments,
  } = useAssignmentsData()
  const { data: appointments } = useClientAppointmentsData()
  const [selectedProgressEpisode, setSelectedProgressEpisode] = useState<{ episode?: Episode }>({
    episode: clientDetailsSelectedEpisode,
  })
  const [isPDFContentInitialized, setIsPDFContentInitialized] = React.useState(false)
  const [selectedRecipientLyraId, setSelectedRecipientLyraId] = useState<string | undefined>(undefined)

  const isProviderCoach = hasRole(providerDetailsData?.roles, LT_COACH_ROLES)
  const isProviderSupervisor = hasRole(providerDetailsData?.roles, SUPERVISOR_ROLES)
  const isProviderLTCoach = hasRole(providerDetailsData?.roles, [
    ROLES.LT_COACH,
    ROLES.LT_CFP_COACH,
    ROLES.LT_NSC_COACH,
    ROLES.LT_CGT_COACH,
  ])

  const isProviderAbleToSeeSharedOutcomes = hasRole(providerDetailsData?.roles, [
    ROLES.LT_CLINICAL_LEAVE_EVALUATOR,
    ROLES.LT_SAP_EVALUATOR,
    ROLES.LT_PRESCRIBER,
    ROLES.LT_PRESCRIBER_SUPERVISOR,
    ROLES.LT_THERAPIST,
    ROLES.LT_SUPERVISOR,
    ROLES.LT_FELLOW_THERAPIST,
    ROLES.LT_FELLOW_SUPERVISOR,
  ])

  const isSelectedEpisodeAUD = selectedProgressEpisode?.episode?.program_name === ProgramNames.AlcoholUseDisorderTherapy

  const supportsMultipleAssessmentRecipients =
    !!selectedProgressEpisode?.episode?.program_name &&
    programConfig[selectedProgressEpisode.episode.program_name as LTProgramName]?.supportsMultipleAssessmentRecipients

  const guardianInfo = useMemo(() => {
    if (!supportsMultipleAssessmentRecipients) return undefined
    const relevantAppointments = selectedProgressEpisode?.episode?.id
      ? appointments?.filter((appt) => appt.episodeId === selectedProgressEpisode.episode!.id)
      : appointments
    return getGuardianInfoForEpisode(relevantAppointments ?? [], selectedProgressEpisode?.episode?.participants)
  }, [appointments, selectedProgressEpisode?.episode, supportsMultipleAssessmentRecipients])

  const recipientOptions = useMemo(() => {
    if (!supportsMultipleAssessmentRecipients || !clientDetails) return undefined
    const teenOption = {
      lyraId: clientDetails.id,
      name: clientDetails.first_name ?? '',
    }
    const caregiverOption = guardianInfo?.guardianLyraId
      ? {
          lyraId: guardianInfo.guardianLyraId,
          name: guardianInfo.guardianFirstName ?? '',
        }
      : undefined
    return { teen: teenOption, caregiver: caregiverOption }
  }, [supportsMultipleAssessmentRecipients, clientDetails, guardianInfo])

  const outcomes = useMemo(() => {
    if (!clientDetails?.outcomes) return undefined

    const useSharedOutcomesFilter =
      ((isSelectedEpisodeAUD && !isProviderCoach) || isProviderAbleToSeeSharedOutcomes) &&
      selectedProgressEpisode?.episode?.state === EpisodeStates.IN_PROGRESS

    const matchesEpisode = (outcome: Outcome) =>
      useSharedOutcomesFilter
        ? !!outcome.response_date &&
          !!outcome.program_name &&
          outcome.episode_id === selectedProgressEpisode?.episode?.id &&
          ![programs.Coaching, programs.SingleSessionCoaching].includes(outcome.program_name)
        : !!outcome.response_date && outcome.episode_id === selectedProgressEpisode?.episode?.id

    const teenOutcomes = clientDetails.outcomes.filter(matchesEpisode)

    // For multi-recipient programs (e.g. DBT Teens) the teen-scoped `outcomes[]` only holds the
    // teen's outcomes; the parent/caregiver's outcomes arrive in a separate field. Merge both into
    // the display array (filtered below by recipient). The PDF and risk badge read
    // `clientDetails.outcomes` directly and stay teen-scoped — they are unaffected by this merge.
    if (!supportsMultipleAssessmentRecipients) return teenOutcomes

    const parentOutcomes = (clientDetails.secondary_patient_outcomes ?? []).filter(matchesEpisode)
    return [...teenOutcomes, ...parentOutcomes]
  }, [
    clientDetails?.outcomes,
    clientDetails?.secondary_patient_outcomes,
    isProviderAbleToSeeSharedOutcomes,
    isProviderCoach,
    isSelectedEpisodeAUD,
    selectedProgressEpisode?.episode,
    supportsMultipleAssessmentRecipients,
  ])

  const filteredOutcomes = useMemo(() => {
    if (!supportsMultipleAssessmentRecipients || !selectedRecipientLyraId) return outcomes
    // Parent outcomes carry `patient_lyra_id = teen` but `recipient_id = parent`, so identify the
    // recipient by `recipient_id`, falling back to `patient_lyra_id` for legacy/single-patient
    // outcomes where `recipient_id` is not set.
    return outcomes?.filter(
      (outcome) =>
        getEffectiveRecipientLyraId(outcome.recipient_id ?? undefined, outcome.patient_lyra_id) ===
        selectedRecipientLyraId,
    )
  }, [outcomes, selectedRecipientLyraId, supportsMultipleAssessmentRecipients])

  const scopedAssignments = useMemo(
    () =>
      assignments?.filter((assignment) => {
        if (assignment.episode_id !== selectedProgressEpisode?.episode?.id) return false
        if (!supportsMultipleAssessmentRecipients || !selectedRecipientLyraId) return true
        return getEffectiveRecipientLyraId(assignment.recipient_id, assignment.patient_id) === selectedRecipientLyraId
      }),
    [assignments, selectedProgressEpisode?.episode?.id, selectedRecipientLyraId, supportsMultipleAssessmentRecipients],
  )

  const nextSessionDate = useMemo(() => {
    const selectedEpisodeId = selectedProgressEpisode?.episode?.id
    if (!appointments || !selectedEpisodeId) return undefined

    const nextAppointment = getCurrentAppointment(
      appointments.filter((appointment) => appointment.episodeId === selectedEpisodeId),
    )
    return nextAppointment ? getDateFromAppointment(nextAppointment) : undefined
  }, [appointments, selectedProgressEpisode?.episode?.id])

  const assignmentIdsFromOutcomes = filteredOutcomes?.map((outcome) => outcome.assignment_id)
  const assignmentsWithSelfReflection = assignments?.filter(
    (assignment) =>
      assignmentIdsFromOutcomes?.includes(assignment.id) &&
      assignment?.assignment_responses?.[0]?.response?.self_reflection,
  )

  useEffect(() => {
    setSelectedProgressEpisode({ episode: clientDetailsSelectedEpisode })
    setSelectedRecipientLyraId(undefined)
  }, [clientDetailsSelectedEpisode])

  useEffect(() => {
    if (recipientOptions && !selectedRecipientLyraId) {
      setSelectedRecipientLyraId(recipientOptions.teen.lyraId)
    }
  }, [recipientOptions, selectedRecipientLyraId])

  useEffect(() => {
    setIsPDFContentInitialized(false)
  }, [pdfContentStartDate, pdfContentEndDate])

  useEffect(() => {
    if (
      !setClientProgressPDFContent ||
      !clientDetails?.outcomes ||
      isPDFContentInitialized ||
      isLoadingEpisodes ||
      !providerEpisodesInfo
    ) {
      return
    }
    setIsPDFContentInitialized(true)
    setClientProgressPDFContent(
      ClientProgressPDF({
        outcomes: clientDetails?.outcomes.filter((outcome) => outcome.provider_lyra_id === providerDetailsData?.id),
        pdfContentStartDate,
        pdfContentEndDate,
        providerEpisodesInfo,
      }),
    )
  }, [
    clientDetails?.outcomes,
    providerEpisodesInfo,
    setClientProgressPDFContent,
    isLoadingEpisodes,
    isPDFContentInitialized,
    pdfContentStartDate,
    pdfContentEndDate,
    providerDetailsData?.id,
  ])

  if (isLoadingEpisodes) {
    return (
      <LoadingContainer>
        <LoadingIndicator size={45} />
      </LoadingContainer>
    )
  }

  const assessmentTrackSettings = () => (
    <AligningContainer>
      <SecondaryButton
        testID={tID('ClientProgress-assessmentTrackButton-update')}
        onPress={() => setAssessmentTrackModalOpen(true)}
        leftIcon={<GearIcon isFilled />}
        text={formatMessage({
          defaultMessage: 'Assessment track settings',
          description: 'assessment track settings button label',
        })}
      />
    </AligningContainer>
  )

  const hasAssessmentTrack =
    (isLtSupervisor || roleConfig?.assessmentTrackSettingsEnabled || isSelectedEpisodeAUD) &&
    selectedProgressEpisode &&
    selectedProgressEpisode?.episode

  const flexibleAssessmentEpisodeId = selectedProgressEpisode?.episode?.id
  const canViewFlexibleAssessmentHistory =
    enableDAFlexibleAssessment &&
    isEngageDAProvider &&
    Boolean(flexibleAssessmentEpisodeId) &&
    isDAEngageProgram(selectedProgressEpisode?.episode?.program_name)
  const canManageFlexibleAssessments =
    canViewFlexibleAssessmentHistory && selectedProgressEpisode?.episode?.state === EpisodeStates.IN_PROGRESS

  return (
    <OuterContainer testID={tID('ClientProgress-tab')} hasMargins={false}>
      <ContentContainer>
        <TopDropdownContainer>
          {/*
              Show the Assessment Track selector if:
                - They are a supervisor (persists across REPLACE_USER), OR
                - Their role config has assessmentTrackSettingsEnabled, OR
                - The currently selected episode IS an AUD program
            */}
          {hasAssessmentTrack && (
            <AssessmentTrackSettingsContainer>
              <View>
                <Modal
                  modalContents={
                    <>
                      <CloseIconContainer onPress={() => setAssessmentTrackModalOpen(false)}>
                        <CloseIcon size={16} />
                      </CloseIconContainer>
                      <AssessmentTrackSettingsModal
                        selectedEpisode={selectedProgressEpisode.episode!}
                        closeModal={() => setAssessmentTrackModalOpen(false)}
                        isProviderLTCoach={isProviderLTCoach}
                      />
                    </>
                  }
                  scrollable={false}
                  disableFullScreenModal
                  disableBottomSheet
                  visible={isAssessmentTrackModalOpen}
                  onRequestClose={() => setAssessmentTrackModalOpen(false)}
                  closeOnScrim
                />
              </View>
            </AssessmentTrackSettingsContainer>
          )}
        </TopDropdownContainer>
        {canViewFlexibleAssessmentHistory ? (
          <ClinicalAssessmentHistory
            assignments={scopedAssignments}
            selfReflectionAssignments={assignments}
            canManageAssessments={canManageFlexibleAssessments}
            clientId={clientDetails?.id}
            clientName={clientDetails?.full_name}
            episodeId={selectedProgressEpisode?.episode?.id}
            recipientId={selectedRecipientLyraId}
            isLoading={isLoadingClientDetails || isLoadingAssignments}
            isAssignmentsLoading={isLoadingAssignments || isFetchingAssignments}
            hasAssignmentsError={hasAssignmentsError}
            nextSessionDate={nextSessionDate}
            isProviderSupervisor={isProviderSupervisor}
            outcomes={filteredOutcomes}
            programTaxonomy={selectedProgressEpisode?.episode?.program_taxonomy}
            providerId={providerDetailsData?.id}
            onAddAssessment={() => setAddAssessmentModalOpen(true)}
          />
        ) : (
          <View>
            <ClientOutcomes
              outcomes={filteredOutcomes}
              assignmentsWithSelfReflection={assignmentsWithSelfReflection}
              clientFullName={clientDetails?.full_name}
              clientFirstName={clientDetails?.first_name}
              selectedProgressEpisode={selectedProgressEpisode?.episode}
              assessmentTrackSettings={hasAssessmentTrack ? assessmentTrackSettings() : undefined}
              recipientOptions={recipientOptions}
              selectedRecipientLyraId={selectedRecipientLyraId}
              onRecipientChange={setSelectedRecipientLyraId}
            />
          </View>
        )}
        {canViewFlexibleAssessmentHistory && flexibleAssessmentEpisodeId && (
          <AddAssessmentModal
            visible={isAddAssessmentModalOpen}
            episodeId={flexibleAssessmentEpisodeId}
            clientName={clientDetails?.first_name ?? clientDetails?.full_name ?? ''}
            nextSessionDate={nextSessionDate}
            assignments={scopedAssignments}
            isAssignmentsLoading={isLoadingAssignments || isFetchingAssignments}
            hasAssignmentsError={hasAssignmentsError}
            onRequestClose={() => setAddAssessmentModalOpen(false)}
            onEditExisting={(flexibleAssessment, assessmentName) => {
              setAddAssessmentModalOpen(false)
              setAssessmentToEdit({ flexibleAssessment, assessmentName })
            }}
          />
        )}
        {assessmentToEdit && (
          <EditAssessmentModal
            visible
            flexibleAssessment={assessmentToEdit.flexibleAssessment}
            assessmentName={assessmentToEdit.assessmentName}
            nextSessionDate={nextSessionDate}
            assignments={scopedAssignments ?? []}
            isAssignmentsLoading={isLoadingAssignments || isFetchingAssignments}
            hasAssignmentsError={hasAssignmentsError}
            onRequestClose={() => setAssessmentToEdit(null)}
          />
        )}
      </ContentContainer>
    </OuterContainer>
  )
}

const mapStateToProps = (state: RootState): any => {
  return {
    providerDetailsData: getProviderDetailsData(state),
  }
}

export default connect(mapStateToProps)(ClientProgress)
